import { Injectable, inject, signal } from '@angular/core';

import { StoredAuthSession } from '../models/auth-api.model';
import { User } from '../models/user.model';
import { LoggerService } from './logger.service';

@Injectable({ providedIn: 'root' })
export class SessionService {
    private static readonly AUTH_SESSION_KEY = 'rbi.auth.session';

    private readonly logger = inject(LoggerService);

    readonly currentUser = signal<User | null>(null);
    readonly isAuthenticated = signal<boolean>(false);

    /** `true` si la sesión vigente vive en `localStorage` ("Recordarme"); `false` → `sessionStorage`. */
    private persistent = false;

    restoreSession(): StoredAuthSession | null {
        const session = this.readStoredSession();
        if (!session) {
            return null;
        }

        // Un access token vencido no cierra la sesión si hay refresh token: el interceptor lo renueva
        // con la primera llamada que responda 401. Así "Recordarme" sobrevive a cerrar la app.
        const expired = !!session.expiresAt && new Date(session.expiresAt) <= new Date();
        if (expired && !session.refreshToken) {
            this.clearSession();
            return null;
        }

        this.currentUser.set(session.user);
        this.isAuthenticated.set(true);
        return session;
    }

    /**
     * `remember` = true guarda la sesión en `localStorage` (sobrevive a cerrar el navegador);
     * false la guarda en `sessionStorage` (solo dura la pestaña). Sin valor, conserva el modo actual
     * para que refrescos de token y cambios de usuario no muevan la sesión de almacén.
     */
    setSession(user: User, token: string, refreshToken: string, expiresAt: string, remember = this.persistent): void {
        this.currentUser.set(user);
        this.isAuthenticated.set(true);

        if (typeof window === 'undefined') {
            return;
        }

        this.persistent = remember;
        const session: StoredAuthSession = { user, token, refreshToken, expiresAt };
        this.activeStorage().setItem(SessionService.AUTH_SESSION_KEY, JSON.stringify(session));
        this.inactiveStorage().removeItem(SessionService.AUTH_SESSION_KEY);
    }

    clearSession(): void {
        this.currentUser.set(null);
        this.isAuthenticated.set(false);

        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(SessionService.AUTH_SESSION_KEY);
            window.sessionStorage.removeItem(SessionService.AUTH_SESSION_KEY);
        }
    }

    getToken(): string {
        return this.readStoredSession()?.token ?? '';
    }

    getRefreshToken(): string {
        return this.readStoredSession()?.refreshToken ?? '';
    }

    getSessionExpiresAt(): string {
        return this.readStoredSession()?.expiresAt ?? '';
    }

    private readStoredSession(): StoredAuthSession | null {
        if (typeof window === 'undefined') {
            return null;
        }

        const found = this.locateStoredSession();
        if (!found) {
            return null;
        }

        try {
            return JSON.parse(found.raw) as StoredAuthSession;
        } catch (error) {
            this.logger.warn('SessionService: Invalid auth session in storage', error);
            found.storage.removeItem(SessionService.AUTH_SESSION_KEY);
            return null;
        }
    }

    /** Busca la sesión guardada (primero la de "Recordarme") y recuerda en qué almacén está. */
    private locateStoredSession(): { storage: Storage; raw: string } | null {
        for (const storage of [window.localStorage, window.sessionStorage]) {
            const raw = storage.getItem(SessionService.AUTH_SESSION_KEY);
            if (raw) {
                this.persistent = storage === window.localStorage;
                return { storage, raw };
            }
        }

        return null;
    }

    private activeStorage(): Storage {
        return this.persistent ? window.localStorage : window.sessionStorage;
    }

    private inactiveStorage(): Storage {
        return this.persistent ? window.sessionStorage : window.localStorage;
    }
}

